import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FieldGroup,
  getFieldIdsOfFieldGroup,
  getFullNameForFieldID,
  getOriginalFieldID,
} from '../../../location-utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COMPLEX_DATA_CONNECTION_FIELDS } from '../../../constants';

export enum SelectedFieldsRadioButton {
  'ALL' = 'All',
  'GMB_REQUIRED_FIELDS' = 'GBP required fields',
  'VISIBLE_ONLY' = 'Visible only',
  'NONE' = 'None',
  'INITIAL' = ''
}

export enum SelectedLocationsRadioButton {
  'ALL' = 'All',
  'FILTERED' = 'Filtered only',
  'SELECTED' = 'Selected only'
}

export interface BulkActionPopupData {
  displayedColumns?: string[];
  areLocationsSelected?: boolean;
  areLocationsFiltered?: boolean;
}

@Component({
  template: '',
})
export abstract class BulkActionPopupComponent implements OnInit, OnDestroy {

  protected bulkActionForm: FormGroup<{
    selectedFields: FormArray<FormControl<boolean>>,
    selectedLocationsRadio?: FormControl<SelectedLocationsRadioButton>
    selectedFieldsRadio?: FormControl<SelectedFieldsRadioButton>
    gxpertsLabel?: FormControl<boolean>
  }>;
  protected readonly componentDestroyed = new Subject<void>();

  protected readonly selectedFieldsRadioDefault: SelectedFieldsRadioButton = SelectedFieldsRadioButton.NONE;
  // radio buttons for which a Form Control will be created and listened to value changes
  protected readonly selectedFieldsRadios: SelectedFieldsRadioButton[] = [
  ];
  // radio buttons for which a Form Control will be created
  protected readonly selectedLocationsRadios: SelectedLocationsRadioButton[] = [
  ];
  // fieldGroup arrangement
  protected readonly columns = [
    [FieldGroup.DEFAULT_DISPLAY, FieldGroup.OPENING_HOURS],
    [FieldGroup.ADDRESS, FieldGroup.MEDIA, FieldGroup.PLACE_ACTION_LINKS],
    [FieldGroup.OTHER],
  ];

  // method for getting all field IDs - initialize in child classes!
  protected getSelectableFields: () => string[];

  protected constructor(
      @Inject(MAT_DIALOG_DATA) public data: BulkActionPopupData,
      public dialogRef: MatDialogRef<BulkActionPopupComponent>,
      protected formBuilder: FormBuilder) {
  }

  ngOnInit() {
    this.buildForm();
    this.initFCHandlers();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Builds the form. Add form.addControl() in child classes to add more controls.
   * @protected
   */
  protected buildForm() {
    this.bulkActionForm = this.formBuilder.group({
      selectedFields: this.formBuilder.array(this.initSelectedFieldsFormControls()),
    });

    if (this.selectedLocationsRadios.length) {
      this.bulkActionForm.addControl('selectedLocationsRadio', new FormControl(this.data.areLocationsSelected ?
          SelectedLocationsRadioButton.SELECTED :
          (this.data.areLocationsFiltered ?
                  SelectedLocationsRadioButton.FILTERED :
                  SelectedLocationsRadioButton.ALL
          )),
      );
    }

    if (this.selectedFieldsRadios.length) {
      this.bulkActionForm.addControl('selectedFieldsRadio', new FormControl(this.selectedFieldsRadioDefault));
    }
  }

  /**
   * Initialize selectedFields FC.
   * @protected
   */
  protected initSelectedFieldsFormControls(): FormControl<boolean>[] {
    return this.getSelectableFields().map((fieldID) =>
        this.selectedFieldsRadioDefault === SelectedFieldsRadioButton.NONE ?
            new FormControl(false) :
            SelectedFieldsRadioButton.VISIBLE_ONLY && this.isVisibleInTable(fieldID) ?
                new FormControl(true) :
                new FormControl(true),
    );
  }

  /**
   * Initialize FC handlers to handle field / radio selections.
   * @protected
   */
  protected initFCHandlers() {
    // all popups react to field (checkbox) selection
    this.initSelectedFieldsHandler();

    if (this.selectedFieldsRadios.length) {
      this.initSelectedFieldsRadioHandler();
    }
  }

  /**
   * Handles 'valueChanges' event
   * @protected
   */
  protected initSelectedFieldsHandler(): void {
    this.getSelectedFieldsFormArray().valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => this.updateSelectedFieldsRadioValue());
  }

  /**
   * Handles 'valueChanges' event
   * @protected
   */
  protected initSelectedFieldsRadioHandler(): void {
    this.bulkActionForm.get('selectedFieldsRadio').valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((radio: SelectedFieldsRadioButton) => this.onSelectedFieldsRadioValueChange(radio));
  }

  /**
   * Make sure the radio button selections correspond to field (checkbox) selections
   * @protected
   */
  protected updateSelectedFieldsRadioValue(): string {
    const enabledFieldFCs = this.getSelectedFieldsFormArray().controls
        .filter(fc => fc.enabled);
    // reset in case none of the below applies
    this.getSelectedFieldsRadioFC().setValue(SelectedFieldsRadioButton.INITIAL, { emitEvent: false });

    if (enabledFieldFCs.length && enabledFieldFCs.every(fc => fc.value)) {
      this.getSelectedFieldsRadioFC().setValue(SelectedFieldsRadioButton.ALL, { emitEvent: false });
      return;
    }

    if (enabledFieldFCs.length && enabledFieldFCs.every(fc => !fc.value)) {
      this.getSelectedFieldsRadioFC().setValue(SelectedFieldsRadioButton.NONE, { emitEvent: false });
      return;
    }

    const visibleSelectableFieldIDs = this.getVisibleSelectableFields();
    const selectedFieldIDs = this.getSelectedFieldIDs();
    if (visibleSelectableFieldIDs?.every((fieldID) => selectedFieldIDs
        .includes(fieldID)) && visibleSelectableFieldIDs?.length === selectedFieldIDs?.length) {
      this.getSelectedFieldsRadioFC().setValue(SelectedFieldsRadioButton.VISIBLE_ONLY,
          { emitEvent: false });
      return;
    }
  }

  /**
   * Select fields based on radio group value
   * @param radio
   */
  protected onSelectedFieldsRadioValueChange(radio: SelectedFieldsRadioButton) {
    if (radio === SelectedFieldsRadioButton.ALL) {
      this.masterToggle(true);
      return;
    }

    if (radio === SelectedFieldsRadioButton.VISIBLE_ONLY) {
      this.visibleInTableToggle();
      return;
    }

    if (radio === SelectedFieldsRadioButton.NONE) {
      this.masterToggle(false);
      return;
    }
  }

  /**
   * Selects or deselects all fields.
   * @param doSelect If true, selects all fields. If false, deselect all fields.
   */
  protected masterToggle(doSelect: boolean) {
    this.getSelectedFieldsFormArray().controls.forEach(fc => {
      if (fc.enabled) {
        fc.setValue(doSelect, { emitEvent: false });
        fc.markAsDirty();
      }
    });
  }

  /**
   * Selects all fields that are visible in address list and deselects those which aren't
   */
  protected visibleInTableToggle() {
    this.getSelectedFieldsFormArray().controls.forEach((fc, index) => {
      if (fc.enabled) {
        fc.setValue(this.getVisibleSelectableFields()?.includes(this.getSelectableFieldIDByIndex(index)), { emitEvent: false });
        fc.markAsDirty();
      }
    });
  }

  /**
   * Toggles all fields within a fieldGroup
   * @param fieldIDs - all fields within a FieldGroup
   */
  protected toggleFieldGroup(fieldIDs: string[]) {
    // if all are checked, uncheck everything and vice versa
    const newValue = !this.allFieldsChecked(fieldIDs);

    fieldIDs.forEach(fieldID => {
      const fieldIndex = this.getSelectableFieldIndexByFieldID(fieldID);

      if (fieldIndex > -1) {
        const fc = this.getSelectedFieldsFormArray().controls[fieldIndex];

        if (fc.enabled) {
          fc.setValue(newValue);
          fc.markAsDirty();
        }
      }
    });
  }

  /**
   * Generate popup return value
   */
  protected generateResult(): void {
  }


  /************************************************************************************************/
  /*                                         HELPERS                                              */
  /************************************************************************************************/

  /**
   * Translates respective form control index into a fieldID
   * @param index
   * @protected
   */
  protected getSelectableFieldIDByIndex(index: number) {
    return this.getSelectableFields()[index];
  }

  /**
   * Returns an index based on the fieldIDs array position. This index serves as the formControlName
   * of the respective fieldID (in HTML template)
   * https://angular.io/guide/reactive-forms#creating-dynamic-forms
   * @param fieldID
   */
  protected getSelectableFieldIndexByFieldID(fieldID: string) {
    return this.getSelectableFields().findIndex((_fieldID) => _fieldID === fieldID);
  }

  /**
   * Get 'selectedFields' Form Array
   */
  protected getSelectedFieldsFormArray() {
    return this.bulkActionForm.get('selectedFields') as FormArray<FormControl<boolean>>;
  }

  /**
   * Get 'selectedFields' Form Array values
   * @protected
   */
  protected getSelectedFieldsFCValues(): boolean[] {
    return this.getSelectedFieldsFormArray().controls
        .map((fc) => fc.value);
  }

  /**
   * Checks whether there are some selected fields.
   */
  protected areFieldsSelected() {
    return this.getSelectedFieldsFCValues().some(fv => fv);
  }

  /**
   * Translates the boolean form control array into a filtered fieldID array of only checked fieldIDs
   * @protected
   */
  protected getSelectedFieldIDs(): string[] {
    const selectedFieldIDs = [];

    this.getSelectedFieldsFCValues().forEach((fcValue, i) => {
      if (fcValue) {
        selectedFieldIDs.push(this.getSelectableFieldIDByIndex(i));
      }
    });

    return selectedFieldIDs;
  }

  /**
   * Gets visible selectable fields by filtering out selectable fields not included in displayed columns.
   */
  protected getVisibleSelectableFields() {
    return this.getSelectableFields()
        .filter(fieldID => {
              // if fieldID is a complex one, make sure all subfields are visible in table
              // e.g.: 'phoneNumbers' = 'primaryPhone', 'additionalPhone'
              if (Object.keys(COMPLEX_DATA_CONNECTION_FIELDS).includes(fieldID)) {
                return COMPLEX_DATA_CONNECTION_FIELDS[fieldID]?.every(_fieldID =>
                    this.data.displayedColumns?.includes(_fieldID));
              }

          return this.data.displayedColumns?.includes(fieldID) ||
                  // getChangeableGmbFields() returns a mix of original / alternative fieldIDs
              this.data.displayedColumns?.includes(getOriginalFieldID(fieldID));
            },
        );
  }

  protected getSelectedLocationsRadioFC(): FormControl<SelectedLocationsRadioButton> {
    return this.bulkActionForm.get('selectedLocationsRadio') as FormControl<SelectedLocationsRadioButton>;
  }

  protected getSelectedFieldsRadioFC(): FormControl<SelectedFieldsRadioButton> {
    return this.bulkActionForm.get('selectedFieldsRadio') as FormControl<SelectedFieldsRadioButton>;
  }

  protected getFieldFCByFieldID(fieldID: string): FormControl<boolean> {
    return this.getSelectedFieldsFormArray().controls
        [this.getSelectableFieldIndexByFieldID(fieldID)];
  }

  protected getFieldFCsByFieldIDs(fieldIDs: string[]): FormControl<boolean>[] {
    return fieldIDs.map(fieldID => this.getFieldFCByFieldID(fieldID));
  }

  /**
   * Gets a list of fieldIds of a specific fieldGroup.
   * @param fieldGroup Group you want to look for.
   */
  protected getFieldIDsByGroup(fieldGroup: FieldGroup): string[] {
    return getFieldIdsOfFieldGroup(fieldGroup, this.getSelectableFields());
  }

  /**
   * Returns a "pretty" name for a field ID
   * @param fieldId
   */
  protected getFieldNameByFieldID(fieldId: string): string {
    return getFullNameForFieldID(fieldId);
  }

  /**
   * Returns if a field is currently visible in location list.
   * @param fieldID ID of the field that needs to be checked
   */
  protected isVisibleInTable(fieldID: string): boolean {
    return this.data.displayedColumns?.includes(fieldID);
  }

  /**
   * Checks whether all fields within a fieldGroup are checked
   * @param fieldIDs - all fields within a FieldGroup
   */
  protected allFieldsChecked(fieldIDs: string[]): boolean {
    const enabledFCs = this.getFieldFCsByFieldIDs(fieldIDs)
        ?.filter(fc => fc.enabled);

    return enabledFCs.length ? enabledFCs?.every(fc => fc.value) : false;
  }

  /**
   * Checks whether some fields within a fieldGroup are checked
   * @param fieldIDs - all fields within a FieldGroup
   */
  protected someFieldsChecked(fieldIDs: string[]): boolean {
    const enabledFCs = this.getFieldFCsByFieldIDs(fieldIDs)
        ?.filter(fc => fc.enabled);

    return enabledFCs.length ? enabledFCs?.some(fc => fc.value) : false;
  }


}
